import { Fragment } from "react";

import { useNotification, useRenderEditor, useRenderNotificationContainer, useRenderPopup } from "../../infrastructure/ui/UIServices";

export interface IAppPortalProps {
    children: React.ReactNode;
}

/** One key per notification element, so a new `createNotification(<…/>)` remounts (and toasts) even with the same type. */
const notificationKeys = new WeakMap<object, number>();
let lastNotificationKey = 0;

function keyOf(content: React.ReactNode): number {
    if (typeof content !== "object" || content === null) return 0;
    let key = notificationKeys.get(content);
    if (key === undefined) {
        key = ++lastNotificationKey;
        notificationKeys.set(content, key);
    }
    return key;
}

/**
 * Hosts what the imperative UIServices flows open (`openPopup`, `openEditor`, `createNotification`). Their content is
 * self-contained now (a library `Dialog`, `FullScreenLayer`, `ConfirmDrawer`, or the toast bridges in `src/ds`), so the
 * portal draws no overlay of its own.
 */
export function AppPortal(props: IAppPortalProps) {
    const { showPopup, popupContent } = useRenderPopup();
    const { showEditor, editorContent } = useRenderEditor();
    const { showNotificationContainer } = useRenderNotificationContainer();
    const { notificationContent } = useNotification();

    return (
        <>
            {props.children}
            {showEditor ? editorContent : null}
            {showPopup ? popupContent : null}
            {showNotificationContainer && notificationContent ? <Fragment key={keyOf(notificationContent)}>{notificationContent}</Fragment> : null}
        </>
    );
}
